
require("./../../node_modules/@module-federation/nextjs-mf/src/include-defaults.js");
import dynamic from 'next/dynamic';
import { GetServerSidePropsContext } from 'next';
import authCookie from 'utils/authCookie';
import SEO from 'global/SEO';
import If from 'global/ErrorHandlers/If';
import sanitizer from 'string-sanitizer';
import streamApi from 'lib/api/stream';
import { AVATAR_URL, SITE_URL } from 'constants/api';
import type { StreamPostData } from '../../@types/stream';

const GuestPostDetail = dynamic(() => import('features/post-detail/guest'));
const PostDetail = dynamic(() => import('features/post-detail'));

interface PostDetailPageProps {
  post?: StreamPostData;
  postid?: number | 'notfound';
  isLoggedIn: boolean;
}

function PostDetailPage({
  post,
  postid,
  isLoggedIn,
}: Readonly<PostDetailPageProps>) {
  const title = `${post?.fullname} (${post?.username}) on Stockbit`;
  const description = post?.content_original;
  const url = `${SITE_URL}/post/${post?.postid}`;
  const image = post?.avatar?.startsWith('http')
    ? post?.avatar
    : `${AVATAR_URL}/${post?.avatar}`;

  const postSEO = {
    title,
    description,
    url,
    image,
  };

  return (
    <>
      <SEO {...postSEO} />
      <If condition={!isLoggedIn}>
        <GuestPostDetail post={post} />
      </If>

      <If condition={isLoggedIn}>
        <PostDetail postId={postid} />
      </If>
    </>
  );
}

async function fetchPostDetail(postid: string) {
  try {
    const response = await streamApi.getGuestStreamDetail(postid);
    if (response.status === 200 && response.data) {
      return response.data.data;
    }

    return Promise.reject(new Error('Post is not found'));
  } catch (error) {
    return Promise.reject(new Error(error));
  }
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { params } = context;
  const { postid } = params;
  const isLoggedIn = Boolean(authCookie.getIsLoggedIn(context));
  const sanitizedPostid = sanitizer.sanitize(postid as string);
  const isNum = /^\d+$/.test(sanitizedPostid);

  try {
    if (sanitizedPostid === 'notfound') {
      throw new Error('Not found post');
    }
    if (!isNum) {
      throw new Error('Not valid post id');
    }

    const post = await fetchPostDetail(sanitizedPostid);
    return {
      props: {
        post,
        postid: sanitizedPostid,
        isLoggedIn,
      },
    };
  } catch (error) {
    if (isLoggedIn) {
      return {
        props: {
          post: null,
          postid: 'notfound',
          isLoggedIn,
        },
      };
    }

    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }
}

export default PostDetailPage;
